/**
 * Customer API Client
 * Handles all customer-related API calls to Route Royal
 */

/** Throttle auth failure logs to avoid console spam when many components fail (e.g. wishlist on each card) */
const AUTH_FAILURE_LOG_THROTTLE_MS = 60_000;
let lastAuthFailureLog = 0;


import type {
    AddToWishlistRequest,
    AssignedAgent,
    AvailabilitySlot,
    Booking,
    ChatMessage,
    ChatSession,
    CompleteKycUploadData,
    ConciergeData,
    CreateAddressRequest,
    CreateBookingRequest,
    CreateEnquiryRequest,
    CreateFeedbackRequest,
    CreateKycCaseRequest,
    CreateOfferRequest,
    CreateSavedSearchRequest,
    CustomerAddress,
    CustomerDocument,
    CustomerPreferences,
    CustomerProfile,
    CustomerSignupRequest,
    CustomerSignupResponse,
    Deal,
    DealAsset,
    DealOpportunity,
    DealParticipant,
    Enquiry,
    Feedback,
    KycCase,
    KycPolicy,
    KycRequirement,
    KycUploadUrlResponse,
    Offer,
    PaginatedResponse,
    Proposal,
    ProposalEngagement,
    RequestKycUploadUrlData,
    SavedSearch,
    UpdateAddressRequest,
    UploadDocumentRequest,
    Wishlist,
    WishlistItem,
    WishlistWithItems,
} from '@/types/customer';
import { BaseAPIClient } from '../base-client';
import { apiCache } from '../cache';

/**
 * Customer API Client for authenticated customer operations
 */
export class CustomerAPIClient extends BaseAPIClient {
  private organisationId: string;
  private accessToken: string | null = null;

  constructor(organisationId: string, baseUrl?: string) {
    super(baseUrl);
    this.organisationId = organisationId;
  }

  /**
   * Set the access token for authenticated requests
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /**
   * Check if we're running in the browser
   */
  private isClientSide(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Make an authenticated request
   * Uses proxy route when running client-side to avoid CORS issues
   */
  private async authenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    if (!this.accessToken) {
      throw new Error('Not authenticated');
    }

    // When running client-side, use the proxy route to avoid CORS
    if (this.isClientSide()) {
      return this.proxyRequest<T>(endpoint, options);
    }

    // Server-side: make direct API calls
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
      Authorization: `Bearer ${this.accessToken}`,
      'X-Organisation-ID': this.organisationId,
    };

    return this.request<T>(endpoint, { ...options, headers }, false);
  }

  /**
   * Make a request through the Next.js proxy route (for client-side calls)
   */
  private async proxyRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const method = (options.method || 'GET') as 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    
    // Parse body if it's a string
    let body: Record<string, unknown> | undefined;
    if (options.body) {
      body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
    }

    const response = await fetch('/api/customer/proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        method,
        body,
        accessToken: this.accessToken,
        organisationId: this.organisationId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const extractString = (val: unknown): string | undefined =>
        typeof val === 'string' ? val : undefined;
      const errorMessage =
        extractString(errorData) ??
        extractString(errorData?.error) ??
        extractString(errorData?.message) ??
        extractString(errorData?.details?.message) ??
        extractString(errorData?.details?.error) ??
        (errorData?.message ? JSON.stringify(errorData.message) : null) ??
        `API request failed: ${response.status}`;
      
      const errorCode = errorData?.code || '';
      
      // Throttle logging for 401/403 auth failures - many components (e.g. wishlist buttons)
      // may fail at once; avoid spamming the console
      const isAuthFailure = response.status === 401 || response.status === 403;
      const shouldLog = !isAuthFailure || (Date.now() - lastAuthFailureLog) > AUTH_FAILURE_LOG_THROTTLE_MS;
      if (shouldLog) {
        if (isAuthFailure) {
          lastAuthFailureLog = Date.now();
          console.warn('[CustomerAPIClient] Auth failure (token may be invalid or Route Royal misconfigured):', {
            status: response.status,
            code: errorCode,
            endpoint,
            errorMessage,
          });
        } else {
          console.error('[CustomerAPIClient] Proxy request failed:', {
            status: response.status,
            code: errorCode,
            endpoint,
            errorMessage,
          });
        }
      }
      
      const error = new Error(errorMessage) as Error & { code?: string; status?: number };
      error.code = errorCode;
      error.status = response.status;
      throw error;
    }

    const json = await response.json();

    // Route Royal wraps some responses in ApiResponseDto ({ success, data }).
    // Unwrap transparently so callers always receive the inner payload.
    if (json && typeof json === 'object' && 'success' in json && 'data' in json) {
      return json.data as T;
    }
    return json as T;
  }

  // ============================================================================
  // Customer Provisioning
  // ============================================================================

  /**
   * Provision a customer in Route Royal after Supabase signup
   * This is called without authentication (customer doesn't have a Route Royal session yet)
   */
  async provisionCustomer(data: CustomerSignupRequest): Promise<CustomerSignupResponse> {
    const headers: Record<string, string> = {
      'X-Organisation-ID': this.organisationId,
    };

    return this.request<CustomerSignupResponse>(
      '/auth/customer/signup',
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...data,
          organisationId: this.organisationId,
        }),
      },
      false
    );
  }

  /**
   * Sync customer session with Route Royal
   * Called on login to ensure customer membership exists for this organisation
   */
  async syncSession(): Promise<CustomerProfile> {
    return this.authenticatedRequest<CustomerProfile>('/users/me/profile');
  }

  // ============================================================================
  // Profile Management
  // ============================================================================

  /**
   * Get current customer profile
   */
  async getProfile(): Promise<CustomerProfile> {
    return this.authenticatedRequest<CustomerProfile>('/users/me/profile');
  }

  /**
   * Update customer profile
   */
  async updateProfile(data: Partial<CustomerProfile>): Promise<CustomerProfile> {
    return this.authenticatedRequest<CustomerProfile>('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get customer preferences
   */
  async getPreferences(): Promise<CustomerPreferences> {
    return this.authenticatedRequest<CustomerPreferences>('/users/me/preferences');
  }

  /**
   * Update customer preferences
   */
  async updatePreferences(data: Partial<CustomerPreferences>): Promise<CustomerPreferences> {
    return this.authenticatedRequest<CustomerPreferences>('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // Wishlists
  // ============================================================================

  /**
   * Get all wishlists for the current customer
   */
  async getWishlists(): Promise<PaginatedResponse<Wishlist>> {
    return this.authenticatedRequest<PaginatedResponse<Wishlist>>('/wishlists');
  }

  /**
   * Get a wishlist with its items
   */
  async getWishlist(wishlistId: string): Promise<WishlistWithItems> {
    return this.authenticatedRequest<WishlistWithItems>(`/wishlists/${wishlistId}`);
  }

  /**
   * Create a new wishlist
   */
  async createWishlist(data: { name: string; description?: string }): Promise<Wishlist> {
    const result = await this.authenticatedRequest<Wishlist>('/wishlists', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    apiCache.invalidateByPattern('/wishlists');
    return result;
  }

  /**
   * Update a wishlist
   */
  async updateWishlist(wishlistId: string, data: Partial<Wishlist>): Promise<Wishlist> {
    return this.authenticatedRequest<Wishlist>(`/wishlists/${wishlistId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a wishlist
   */
  async deleteWishlist(wishlistId: string): Promise<void> {
    await this.authenticatedRequest<void>(`/wishlists/${wishlistId}`, {
      method: 'DELETE',
    });
    apiCache.invalidateByPattern('/wishlists');
  }

  /**
   * Add item to a wishlist
   */
  async addToWishlist(wishlistId: string, data: AddToWishlistRequest): Promise<WishlistItem> {
    const result = await this.authenticatedRequest<WishlistItem>(`/wishlists/${wishlistId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    apiCache.invalidateByPattern('/wishlists');
    return result;
  }

  /**
   * Quick add to default wishlist
   */
  async quickAddToWishlist(data: AddToWishlistRequest): Promise<{ wishlistId: string; itemId: string }> {
    const result = await this.authenticatedRequest<{ wishlistId: string; itemId: string }>('/wishlists/quick-add', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    apiCache.invalidateByPattern('/wishlists');
    return result;
  }

  /**
   * Remove item from a wishlist
   */
  async removeFromWishlist(wishlistId: string, itemId: string): Promise<void> {
    await this.authenticatedRequest<void>(`/wishlists/${wishlistId}/items/${itemId}`, {
      method: 'DELETE',
    });
    apiCache.invalidateByPattern('/wishlists');
  }

  /**
   * Check if an entity is in any wishlist
   */
  async isInWishlist(entityType: string, entityId: string): Promise<{ isInWishlist: boolean; wishlistIds: string[] }> {
    return this.authenticatedRequest<{ isInWishlist: boolean; wishlistIds: string[] }>(
      `/wishlists/check?entityType=${entityType}&entityId=${entityId}`
    );
  }

  // ============================================================================
  // Offers
  // ============================================================================

  /**
   * Get all offers for the current customer
   */
  async getOffers(): Promise<PaginatedResponse<Offer>> {
    return this.authenticatedRequest<PaginatedResponse<Offer>>('/offers');
  }

  /**
   * Get a specific offer
   */
  async getOffer(offerId: string): Promise<Offer> {
    return this.authenticatedRequest<Offer>(`/offers/${offerId}`);
  }

  /**
   * Create a new offer
   */
  async createOffer(data: CreateOfferRequest): Promise<Offer> {
    return this.authenticatedRequest<Offer>('/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update a draft offer
   */
  async updateOffer(offerId: string, data: Partial<CreateOfferRequest>): Promise<Offer> {
    return this.authenticatedRequest<Offer>(`/offers/${offerId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Submit an offer
   */
  async submitOffer(offerId: string): Promise<Offer> {
    return this.authenticatedRequest<Offer>(`/offers/${offerId}/submit`, {
      method: 'POST',
    });
  }

  /**
   * Withdraw an offer
   */
  async withdrawOffer(offerId: string, reason?: string): Promise<Offer> {
    return this.authenticatedRequest<Offer>(`/offers/${offerId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Delete a draft offer
   */
  async deleteOffer(offerId: string): Promise<void> {
    await this.authenticatedRequest<void>(`/offers/${offerId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Enquiries
  // ============================================================================

  /**
   * Get all enquiries for the current customer
   * Uses the customer-facing endpoint that filters by the authenticated user
   */
  async getEnquiries(): Promise<PaginatedResponse<Enquiry>> {
    return this.authenticatedRequest<PaginatedResponse<Enquiry>>('/enquiries/customer/mine');
  }

  /**
   * Get a specific enquiry with messages
   */
  async getEnquiry(enquiryId: string): Promise<Enquiry> {
    return this.authenticatedRequest<Enquiry>(`/enquiries/${enquiryId}`);
  }

  /**
   * Create a new enquiry via the customer-facing submit endpoint
   */
  async createEnquiry(data: CreateEnquiryRequest): Promise<Enquiry> {
    return this.authenticatedRequest<Enquiry>('/enquiries/customer/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Add a message to an enquiry
   */
  async addEnquiryMessage(enquiryId: string, content: string): Promise<Enquiry> {
    return this.authenticatedRequest<Enquiry>(`/enquiries/${enquiryId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  // ============================================================================
  // Bookings (Customer-Facing Endpoints)
  // ============================================================================
  // These use the customer-facing booking endpoints pattern:
  // - GET  /bookings/customer/mine          - Get customer's own bookings
  // - POST /bookings/customer/request       - Request a booking (inspection/viewing)
  // - GET  /bookings/customer/:id           - Get a specific booking
  // - POST /bookings/customer/:id/cancel    - Cancel a booking

  /**
   * Get availability slots for booking
   */
  async getAvailability(params: {
    propertyId?: string;
    listingId?: string;
    bookingType: string;
    startDate: string;
    endDate: string;
  }): Promise<AvailabilitySlot[]> {
    const searchParams = new URLSearchParams();
    if (params.propertyId) searchParams.append('propertyId', params.propertyId);
    if (params.listingId) searchParams.append('listingId', params.listingId);
    searchParams.append('bookingType', params.bookingType);
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);

    return this.authenticatedRequest<AvailabilitySlot[]>(`/bookings/customer/availability?${searchParams.toString()}`);
  }

  /**
   * Get all bookings for the current customer
   * Uses customer-facing endpoint: GET /bookings/customer/mine
   */
  async getBookings(): Promise<PaginatedResponse<Booking>> {
    return this.authenticatedRequest<PaginatedResponse<Booking>>('/bookings/customer/mine');
  }

  /**
   * Get a specific booking
   * Uses customer-facing endpoint: GET /bookings/customer/:id
   */
  async getBooking(bookingId: string): Promise<Booking> {
    return this.authenticatedRequest<Booking>(`/bookings/customer/${bookingId}`);
  }

  /**
   * Request a new booking (inspection/viewing)
   * Uses customer-facing endpoint: POST /bookings/customer/request
   */
  async createBooking(data: CreateBookingRequest): Promise<Booking> {
    return this.authenticatedRequest<Booking>('/bookings/customer/request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Cancel a booking
   * Uses customer-facing endpoint: POST /bookings/customer/:id/cancel
   */
  async cancelBooking(bookingId: string, reason?: string): Promise<Booking> {
    return this.authenticatedRequest<Booking>(`/bookings/customer/${bookingId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Reschedule a booking
   * Uses customer-facing endpoint: POST /bookings/customer/:id/reschedule
   */
  async rescheduleBooking(bookingId: string, newScheduledAt: string): Promise<Booking> {
    return this.authenticatedRequest<Booking>(`/bookings/customer/${bookingId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduledAt: newScheduledAt }),
    });
  }

  // ============================================================================
  // Documents
  // ============================================================================

  /**
   * Get all documents for the current customer
   */
  async getDocuments(): Promise<PaginatedResponse<CustomerDocument>> {
    return this.authenticatedRequest<PaginatedResponse<CustomerDocument>>('/documents');
  }

  /**
   * Get a specific document
   */
  async getDocument(documentId: string): Promise<CustomerDocument> {
    return this.authenticatedRequest<CustomerDocument>(`/documents/${documentId}`);
  }

  /**
   * Create a document record (after uploading to storage)
   */
  async createDocument(data: UploadDocumentRequest): Promise<CustomerDocument> {
    return this.authenticatedRequest<CustomerDocument>('/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: string): Promise<void> {
    await this.authenticatedRequest<void>(`/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // KYC
  // ============================================================================

  /**
   * Get available KYC policies for this organisation
   */
  async getKycPolicies(subjectType?: string): Promise<PaginatedResponse<KycPolicy>> {
    const params = new URLSearchParams({ isActive: 'true' });
    if (subjectType) params.append('subjectType', subjectType);
    return this.authenticatedRequest<PaginatedResponse<KycPolicy>>(
      `/organisations/${this.organisationId}/kyc/policies?${params.toString()}`
    );
  }

  /**
   * Get KYC cases for the current customer
   */
  async getKycCases(): Promise<PaginatedResponse<KycCase>> {
    return this.authenticatedRequest<PaginatedResponse<KycCase>>(`/organisations/${this.organisationId}/kyc/cases`);
  }

  /**
   * Get a specific KYC case
   */
  async getKycCase(caseId: string): Promise<KycCase> {
    return this.authenticatedRequest<KycCase>(`/organisations/${this.organisationId}/kyc/cases/${caseId}`);
  }

  /**
   * Create a new KYC case with the required policy and subject info
   */
  async createKycCase(data: CreateKycCaseRequest): Promise<KycCase> {
    return this.authenticatedRequest<KycCase>(`/organisations/${this.organisationId}/kyc/cases`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Submit a KYC case for review
   */
  async submitKycCase(caseId: string): Promise<KycCase> {
    return this.authenticatedRequest<KycCase>(`/organisations/${this.organisationId}/kyc/cases/${caseId}/submit`, {
      method: 'POST',
    });
  }

  /**
   * Get all requirements for a KYC case
   */
  async getKycRequirements(caseId: string): Promise<KycRequirement[]> {
    const response = await this.authenticatedRequest<{ data: KycRequirement[] } | KycRequirement[]>(
      `/organisations/${this.organisationId}/kyc/cases/${caseId}/requirements`
    );
    return Array.isArray(response) ? response : response.data ?? [];
  }

  /**
   * Request a signed upload URL for a KYC document requirement
   */
  async getKycRequirementUploadUrl(
    caseId: string,
    requirementId: string,
    data: RequestKycUploadUrlData
  ): Promise<KycUploadUrlResponse> {
    return this.authenticatedRequest<KycUploadUrlResponse>(
      `/organisations/${this.organisationId}/kyc/cases/${caseId}/requirements/${requirementId}/upload-url`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  /**
   * Complete a KYC document upload after file has been uploaded to storage
   */
  async completeKycUpload(
    caseId: string,
    requirementId: string,
    data: CompleteKycUploadData
  ): Promise<KycRequirement> {
    return this.authenticatedRequest<KycRequirement>(
      `/organisations/${this.organisationId}/kyc/cases/${caseId}/requirements/${requirementId}/complete-upload`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  // ============================================================================
  // Addresses (Location System)
  // ============================================================================

  /**
   * Get all addresses for the current customer
   */
  async getAddresses(): Promise<CustomerAddress[]> {
    return this.authenticatedRequest<CustomerAddress[]>('/users/profile/contact-ddd/addresses');
  }

  /**
   * Create a new address for the current customer
   */
  async createAddress(data: CreateAddressRequest): Promise<CustomerAddress> {
    return this.authenticatedRequest<CustomerAddress>('/users/profile/contact-ddd/addresses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an existing address
   */
  async updateAddress(linkId: string, data: UpdateAddressRequest): Promise<CustomerAddress> {
    return this.authenticatedRequest<CustomerAddress>(`/users/profile/contact-ddd/addresses/${linkId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Set an address as the primary address
   */
  async setPrimaryAddress(linkId: string): Promise<{ success: boolean }> {
    return this.authenticatedRequest<{ success: boolean }>(`/users/profile/contact-ddd/addresses/${linkId}/primary`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
  }

  /**
   * Delete an address
   */
  async deleteAddress(linkId: string): Promise<{ success: boolean }> {
    return this.authenticatedRequest<{ success: boolean }>(`/users/profile/contact-ddd/addresses/${linkId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Concierge / Agent Assignment
  // ============================================================================

  /**
   * Get the customer's assigned concierge/agent
   * This returns the agent assigned to this customer for personalized service
   */
  async getConcierge(): Promise<ConciergeData> {
    return this.authenticatedRequest<ConciergeData>('/users/me/concierge');
  }

  /**
   * Get the customer's assigned agent from their enquiries
   * This derives the agent from existing customer interactions
   */
  async getAssignedAgentFromEnquiries(): Promise<AssignedAgent | null> {
    try {
      const enquiriesResponse = await this.getEnquiries();
      
      for (const enquiry of enquiriesResponse.items) {
        const agentMessage = enquiry.messages?.find(m => m.senderType === 'AGENT');
        if (agentMessage) {
          return null;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  // ============================================================================
  // Proposals
  // ============================================================================

  async getProposals(): Promise<Proposal[]> {
    return this.authenticatedRequest<Proposal[]>('/proposals');
  }

  async getProposal(id: string): Promise<Proposal> {
    return this.authenticatedRequest<Proposal>(`/proposals/${id}`);
  }

  async getProposalByToken(token: string): Promise<Proposal> {
    return this.request<Proposal>(`/proposals/token/${token}`);
  }

  async trackProposalEngagement(data: ProposalEngagement): Promise<void> {
    await this.authenticatedRequest<void>('/proposals/engagement', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // Deals (Extended)
  // ============================================================================

  async getDeals(): Promise<Deal[]> {
    return this.authenticatedRequest<Deal[]>('/deals');
  }

  async getDeal(id: string): Promise<Deal> {
    return this.authenticatedRequest<Deal>(`/deals/${id}`);
  }

  async getDealParticipants(dealId: string): Promise<DealParticipant[]> {
    return this.authenticatedRequest<DealParticipant[]>(`/deals/${dealId}/participants`);
  }

  async getDealAssets(dealId: string): Promise<DealAsset[]> {
    return this.authenticatedRequest<DealAsset[]>(`/deals/${dealId}/assets`);
  }

  async getDealOpportunities(dealId: string): Promise<DealOpportunity[]> {
    return this.authenticatedRequest<DealOpportunity[]>(`/deals/${dealId}/opportunities`);
  }

  // ============================================================================
  // Feedback
  // ============================================================================

  async getFeedback(): Promise<PaginatedResponse<Feedback>> {
    return this.authenticatedRequest<PaginatedResponse<Feedback>>('/feedback');
  }

  async createFeedback(data: CreateFeedbackRequest): Promise<Feedback> {
    return this.authenticatedRequest<Feedback>('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFeedbackItem(id: string): Promise<Feedback> {
    return this.authenticatedRequest<Feedback>(`/feedback/${id}`);
  }

  // ============================================================================
  // Saved Searches
  // ============================================================================

  async getSavedSearches(): Promise<SavedSearch[]> {
    return this.authenticatedRequest<SavedSearch[]>('/users/me/saved-searches');
  }

  async createSavedSearch(data: CreateSavedSearchRequest): Promise<SavedSearch> {
    return this.authenticatedRequest<SavedSearch>('/users/me/saved-searches', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteSavedSearch(id: string): Promise<void> {
    await this.authenticatedRequest<void>(`/users/me/saved-searches/${id}`, {
      method: 'DELETE',
    });
  }

  // ============================================================================
  // Chat
  // ============================================================================

  async getChatSessions(): Promise<ChatSession[]> {
    return this.authenticatedRequest<ChatSession[]>('/chat/sessions');
  }

  async createChatSession(): Promise<ChatSession> {
    return this.authenticatedRequest<ChatSession>('/chat/sessions', { method: 'POST', body: '{}' });
  }

  async getChatMessages(sessionId: string): Promise<ChatMessage[]> {
    return this.authenticatedRequest<ChatMessage[]>(`/chat/sessions/${sessionId}/messages`);
  }

  async sendChatMessage(sessionId: string, content: string): Promise<ChatMessage> {
    return this.authenticatedRequest<ChatMessage>(`/chat/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }
}

/**
 * Create a customer API client with organisation context
 */
export function createCustomerClient(organisationId: string): CustomerAPIClient {
  return new CustomerAPIClient(organisationId);
}
